import React, { useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import IconButton from 'components/common/IconButton';
import PurchaseSection from './components/PurchaseSection';
import {
  getStockPurchases,
  getBeSpokePurchases,
  exportBeSpokePurchases,
  exportStockPurchases
} from 'services/purchasing-history/purchasingHistoryService';
import { searchSuppliers } from 'services/supplier/supplierDashboardService';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PurchasingHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [stockSupplierId, setStockSupplierId] = useState('');
  const [bespokeSupplierId, setBespokeSupplierId] = useState('');
  const previousSupplierId =
    searchParams.get('supplierId') || stockSupplierId || bespokeSupplierId;
  const activeSupplierId = bespokeSupplierId || stockSupplierId;
  const activeSupplier = suppliers.find(
    supplier => String(supplier.supplierId) === String(activeSupplierId)
  );
  const isEvaSupplier =
    String(activeSupplier?.importDataFrom || '').toUpperCase() === 'EVA';
  const stockSupplierOptions = activeSupplierId
    ? suppliers.filter(
        supplier =>
          String(supplier.importDataFrom || '').toUpperCase() !== 'EVA'
      )
    : suppliers;
  const handleSupplierSelection = supplierId => {
    const selectedSupplier = suppliers.find(
      supplier => String(supplier.supplierId) === String(supplierId)
    );

    if (
      String(selectedSupplier?.importDataFrom || '').toUpperCase() === 'EVA'
    ) {
      setBespokeSupplierId(supplierId);
      setStockSupplierId('');
      return;
    }

    setStockSupplierId(supplierId);
    setBespokeSupplierId('');
  };

  useEffect(() => {
    const loadSuppliers = async () => {
      const res = await searchSuppliers({
        pageNum: 1,
        pageSize: 1000,
        isLike: true,
        isGetAll: true,
        keyword: ''
      });

      setSuppliers(res?.items || []);
    };

    loadSuppliers();
  }, []);

  useEffect(() => {
    const supplierId = searchParams.get('supplierId') || '';
    if (!supplierId || suppliers.length === 0) {
      return;
    }

    handleSupplierSelection(supplierId);
  }, [searchParams, suppliers]);

  return (
    <>
      <PageHeader
        title="Purchasing History"
        description="View supplier purchasing history"
        className="mb-3"
        endEl={
          <div className="d-flex gap-2">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="chevron-left"
              transform="shrink-3"
              onClick={() => navigate('/supplier/supplier-dashboard')}
            >
              Back to Supplier Dashboard
            </IconButton>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="arrow-left"
              transform="shrink-3"
              onClick={() => {
                if (previousSupplierId) {
                  navigate(`/supplier/supplier-details/${previousSupplierId}`);
                  return;
                }
                navigate('/supplier/supplier-dashboard');
              }}
            >
              Back to Supplier
            </IconButton>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="boxes"
              transform="shrink-3"
              onClick={() => {
                if (previousSupplierId) {
                  navigate(`/supplier/supplier-products/${previousSupplierId}`);
                  return;
                }
                navigate('/supplier/supplier-dashboard');
              }}
            >
              Go to Supplier Products / Vendor Master
            </IconButton>
          </div>
        }
      />

      <Row className="g-3">
        <Col lg={12}>
          <Card body>
            {isEvaSupplier ? (
              <PurchaseSection
                title="Bespoke Purchases"
                fetchApi={getBeSpokePurchases}
                exportApi={exportBeSpokePurchases}
                supplierId={bespokeSupplierId}
                supplierOptions={suppliers.filter(
                  supplier =>
                    String(supplier.importDataFrom || '').toUpperCase() ===
                    'EVA'
                )}
                supplierLabel="EVA Source Suppliers (Supplier Code)"
                onSupplierChange={handleSupplierSelection}
              />
            ) : (
              <PurchaseSection
                title="Stock Purchases"
                fetchApi={getStockPurchases}
                exportApi={exportStockPurchases}
                supplierId={stockSupplierId}
                supplierOptions={stockSupplierOptions}
                supplierLabel="Supplier"
                onSupplierChange={handleSupplierSelection}
              />
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PurchasingHistory;
